/// region Store

import {React, _, useContext} from '@api'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {GroupDetailAggregationConfig, GroupDetailAggregationFieldConfig, onSiteError, StoresInstance} from '@stores'
import {removeUndefinedsNested} from '@utils'
import {action, computed, observable, runInAction} from 'mobx'

export const AggregateDetailsStoreContext = React.createContext<AggregateDetailsStore>(undefined)
export const useAggregateDetailsStore = () => useContext(AggregateDetailsStoreContext)

export class AggregateDetailsStore extends VipLocalStore{
  constructor(stores: StoresInstance,private groupPath: string[]) {
    super(stores)

    const {groupBy} = this
    this.aggregationFilterStore = new AggregationFilterStore(stores, this.config, this.config?.aggregationFilterField)

    if (!groupPath) {
      groupBy.aggregationDetails = this
    } else {
      groupBy.groupAggregationStores.set(groupPath.join('|'), this)
    }
    this._aggregationGroupPath = groupPath
  }

  @observable _aggregationGroupPath: string[]

  aggregationFilterStore: AggregationFilterStore

  get configs() { return this.catalog.catalogConfig?.features?.groupBy?.detailAggregations ?? []}

  get config(): GroupDetailAggregationConfig { return _.first(this.configs)}

  @computed get aggregationFields() {
    const {config, catalog} = this
    const r = _.flatMap(config?.sections.map(s =>
      (s?.fields ?? [])
        .filter(f => f.field || f.output).map(aggField => ({aggField, catalogField: catalog._fieldByName[aggField.field]}))
        .filter(f => _.identity(f) && !f.catalogField?.aggDetails?.computed && !f.catalogField?.aggDetails?.agg && !f.aggField.computed))
    )

    const grouped = _.groupBy(r, r => r.aggField.output ?? r.aggField.field)
    _.keys(grouped).forEach(g => {
      const occurrences = grouped[g].length
      if (occurrences > 1) {
        console.error(`The field ${g} shows up more than one time (${occurrences}) - you MUST specify an 'output' to differentiate between fields of the same name with different aggregations (${grouped[g].map(v => v.aggField.agg ?? 'sum').join(', ')})`)
      }
    })
    return r
  }

  @computed get _source() {
    const {config} = this
    return _.uniq(
      _.flatMap([
        ...(config?.sections?.map(s => _.flatMap((s.fields ?? []).map(f => [f?.field, ...(f?.computed?.deps ?? [])]))) ?? []),
        // ...(config?.selection?.items?.map(s => _.flatMap((s.fields ?? []).map(f => f.esField))) ?? []),
        ...(config?.aggregationDeps ?? [])
      ]).filter(f => _.identity(f)))
  }

  @observable loading = false
  @observable.ref hits: Array<{ _id } & any> = []

  @computed get aggregationColumns(): Array<any> {
    const {configs: aggregations} = this

    return aggregations.reduce((acc, aggs) => {
      const fields = _.flatMap(aggs.sections, 'fields')
      return [
        ...acc,
        ...fields.map(field => {
          if (_.isObject(field.agg) || field.aggFn || (field.agg === 'value_count' && field.filter)) return field
        })
      ].filter(_.identity)
    }, [])
  }

  @computed get generateAggregations() {
    const {aggregationColumns, aggregationFilterStore: {selectedAggFilters, availableAggFilterValues}, catalog} = this

    return (aggregationColumns.reduce((acc, v) => ({
      ...acc, ...(_.isFunction(v.aggFn) ? v.aggFn(selectedAggFilters.length ? selectedAggFilters : availableAggFilterValues) : (v.agg && !v.filter ? {[`${v.output ?? v.name}`]: v.aggFn ?? v.agg} : v.filter ? {
        [`${v.output ?? v.name}`]: {
          nested: {path: v.nestedPath},
          aggs: {
            inner: {
              ...(v.filter ? {
                  filter: _.isFunction(v.filter) ? v.filter([], availableAggFilterValues ?? []) : v.filter
                }
                           : {}),
              aggs: {
                assorted: {
                  reverse_nested: {},
                  aggs: {
                    final: _.isFunction(v?.assortmentFilter) ? {filter: v.assortmentFilter()} : {value_count: {field: '_id'}}
                  }
                }
              }
            }

          }
        }
      } : {}))
    }), {}))

    /*return aggregationFields.reduce((acc, {aggField, catalogField}, i) => {
      // console.log({selectedAggFilters, availableAggFilterValues, aggField})
      const agg = {[aggField.agg ? aggField.agg : 'sum']: {field: aggField.field ?? '_id'}},
        sectionConfig = catalog.sectionConfigFor(catalogField?.section),
        nestedPath = aggField.nestedPath,
        nestedPathAggregation = {
          nested: {path: nestedPath},
          aggs: {
            inner: {
              ...(aggField.filter ? {
                  filter: _.isFunction(aggField.filter) ? aggField.filter(selectedAggFilters, availableAggFilterValues ?? []) : aggField.filter
                }
                                  : {}),
              aggs: {
                assorted: {
                  reverse_nested: {},
                  aggs: {
                    final: _.isFunction(aggField?.assortmentFilter) ? {filter: aggField.assortmentFilter()} : {value_count: {field: aggField.valueField ?? '_id'}}
                  }
                }
              }
            }

          }
        }

      acc[i] = nestedPath ? (aggField?.isInnerAgg !== false ? nestedPathAggregation : {
        nested: {path: nestedPath},
        ...(aggField.filter ? {
            filter: _.isFunction(aggField.filter) ? aggField.filter(selectedAggFilters, availableAggFilterValues ?? []) : aggField.filter
          }
                            : {}),
        aggs: {
          0: agg
        }
      }) : (aggField.filter ? {
        filter: _.isFunction(aggField.filter) ? aggField.filter(selectedAggFilters, availableAggFilterValues ?? []) : aggField.filter,
        aggs: {
          0: agg
        }
      } : agg)

      return acc
    }, {})*/
  }

  @observable.ref aggregations: any = {}

  @computed get computationalFields() {
    const {config, catalog} = this
    return _.flatMap(config?.sections.map(s =>
      (s?.fields ?? [])
        .map(aggField => ({aggField, catalogField: catalog._fieldByName[aggField.field]}))
        .filter(f => _.identity(f) && (f.aggField.computed || f.catalogField?.aggDetails?.computed || f.catalogField?.aggDetails?.agg)))
    )
  }

  @computed get data() {
    const {aggregations, aggregationFields, aggregationColumns, computationalFields, hits, aggregationFilterStore: {selectedAggFilters, availableAggFilterValues}} = this,
      catalogValues = aggregationFields.reduce((acc, {aggField, catalogField}, i) => {
        if (aggregations[i]) {
          let value = 0
          if (aggField.nestedPath && aggField?.isInnerAgg !== false) {
            value = _.get(aggregations[i], 'inner.assorted.final.value') ?? _.get(aggregations[i], 'inner.assorted.final.doc_count')
          } else {
            const v = aggregations[i]
            value = _.has(v, 'doc_count') ? v[0].value : v.value
          }
          _.set(acc, aggField.output ?? aggField.field, value)
        }
        return acc
      }, {})

    /*let recompute = !_.isEmpty(catalogValues),
      result = catalogValues,
      tries = 0

    while (recompute || _.size(hits) && tries < 5) {
      const nextResult = {
        ...result,
        ...computationalFields.reduce((acc, {aggField, catalogField}) => {
          const computedDef = catalogField?.aggDetails?.computed ?? aggField?.computed
          if (computedDef?.compute && (!computedDef.deps || computedDef.deps?.every(f => _.has(result, f)))) {
            const computeValue = computedDef.compute(result)
            _.set(acc, aggField.output ?? aggField.label ?? aggField.field, computeValue)
          } else if (computedDef?.reduce) {
            // console.log({hits}, this.selectedAggFilters)
            const selectedDropdown = selectedAggFilters?.length > 0 ? selectedAggFilters : availableAggFilterValues ?? [],
              computeValue = computedDef.reduce(hits, selectedDropdown)
            _.set(acc, aggField.output ?? aggField.label ?? aggField.field, computeValue)
          } else if (computedDef?.compute) {
            // console.log(`Unable to call compute() on ${aggField.field ?? aggField.output ?? aggField.label} due to missing deps: ${computedDef.deps?.filter(f => !_.has(result, f)).join(', ')}`)
            // console.log({result, catalogValues})
          }
          return acc
        }, {})
      }

      recompute = !_.isEqual(result, nextResult)
      //console.log({recompute, result, nextResult})
      result = nextResult
      tries++
    }*/

    //console.log({aggregations})
    return aggregationColumns.reduce((acc, {field, name, output, label, dataPath}) => {
      const f = output ?? name ?? label ?? field
      if (aggregations[f]) {
        _.set(acc, f, _.get(aggregations[f], dataPath ?? 'value'))
      }
      return acc
    }, {})
  }

  @computed get groupNode() {
    const {_aggregationGroupPath, catalog, catalog: {groupBy}} = this
    if (!_aggregationGroupPath) { return catalog.aggregation.aggregateDetailsGroup} else {
      const {hierarchyGroups} = groupBy, path = _aggregationGroupPath
      if (path && path.length <= hierarchyGroups.length) {
        const grouping = hierarchyGroups[path.length - 1]
        if (!grouping?.nodes) {
          return null
        }

        return grouping.nodes.get(grouping.pathLookup(null, path))
      }
    }

    return null
  }

  @computed get productQueryVariables() {
    const {catalog: {isProductView, queryVariablesEs}, groupNode} = this

    if (isProductView) return queryVariablesEs
    return groupNode?.store?.childGroupStore?.productQueryVariables
  }

  @computed get queryClause() {
    const {config: {filter}, productQueryVariables} = this

    return _.isEmpty(productQueryVariables) && _.isEmpty(filter)
           ? {}
           : removeUndefinedsNested(
        {
          query: {
            bool: {
              filter: _.isEmpty(filter) ? undefined : filter,
              must: _.isEmpty(productQueryVariables) ? undefined : productQueryVariables.query
            }
          }
        })
  }

  @action runQuery = async () => {
    const {groupNode, catalog} = this
    if (!this.config || (catalog.isGroupedView && !groupNode?.store) || catalog.loadingCounts) return
    this.loading = true

    try {
      const {generateAggregations, queryClause, es} = this

      // console.log(toJS(aggregationFields))
      const {aggregations, items} = es.unwrapHitsResult(await es.search({
        index: catalog.index,
        meta: `GroupAggregateDetailsSidebarContent.runQuery Get field data for aggregate details - ${catalog.isGroupedView ? `group '${groupNode.store?.groupTitleLabel}'` : `${catalog.catalogItemName}`}`,
        body: {
          size: 0, /*TODO Piyush - this fix will break the UR aggregation sidebar. will fix by replace this code with report view aggregation query.
                       Report View code needs some changes before replacing the changes here.
                       */
          ...queryClause,
          aggs: {
            composite: {
              composite: {
                sources: [{
                  Active: {
                    terms: {
                      field: "attributes.Active"
                    }
                  }
                }]
              },
              aggs: {...generateAggregations}
            }
          },
          _source: ['_id', 'exId', ...this._source]
        }
      }, {log: {query: false, result: false}}))

      //console.log(productQueryVariables, groupStore, aggregations)
      runInAction(() => {
        this.aggregations = this._sumAllBuckets(aggregations?.composite?.buckets)
        this.hits = items
      })
    } catch (err) {
      onSiteError(err, false)
    } finally {
      this.loading = false
    }
  }

  @action private _sumAllBuckets = (buckets) => {
    const {aggregationColumns} = this
    return buckets.reduce((acc, bucket) => {
      aggregationColumns.map(column => {
        const key = column.output,
          dataPath = column.dataPath ?? 'value'
        if (!acc[key]) {
          acc[key] = {}
        }
        acc[key] = _.set(acc[key], dataPath, (_.get(acc[key], dataPath) ?? 0) + (_.get(bucket, `${key}.${dataPath}`) ?? 0))
      })
      return acc
    }, {})
  }

  @computed get showNavButtons() {
    const {groupNode} = this

    return groupNode?.store && groupNode.store.grouping.nodes.size > 1
  }

  @action navigateToPreviousGroup = () => {
    const {groupNode: group, catalog} = this,
      {store: {grouping: {groupsInOrder}}} = group,
      index = groupsInOrder.indexOf(group),
      newGroup = groupsInOrder[index == 0 ? groupsInOrder.length - 1 : index - 1]

    catalog.aggregation.toggleAggregationGroup(newGroup.store.pathIncludingSelfKeys)
    newGroup.store.scrollIntoView()
  }

  @action navigateToNextGroup = () => {
    const {groupNode: {store: group}, catalog} = this, {nextGroup} = group

    catalog.aggregation.toggleAggregationGroup(nextGroup.store.pathIncludingSelfKeys)
    nextGroup.store.scrollIntoView()
  }

  @computed get title() {
    const {catalog} = this
    if (catalog.isGroupedView) {
      const {groupNode} = this
      return groupNode?.store?.props?.info?.title
    }
    return catalog.catalogItemName
  }

  @computed get count() {
    const {catalog} = this
    if (catalog.isGroupedView) {
      const {groupNode} = this
      return groupNode?.store?.props?.info?.count
    }
    return catalog.itemCount
  }

  formatNumberValue = ({def, field, output, label}: GroupDetailAggregationFieldConfig) => {
    const style = /*def?.currency ? 'currency' :*/ def?.suffix == '%' ? 'percent' : 'decimal',
      currency = def?.currency,
      precision = def?.precision ?? (def?.type == 'Int' ? 0 : 2),
      formatter = new Intl.NumberFormat(undefined, {currency, minimumFractionDigits: precision, maximumFractionDigits: precision})

    return value => {
      return formatter.format(value)
    }
  }
}

interface IAggFilter {
  id: string,
  label: string
}

export class AggregationFilterStore extends VipLocalStore {
  constructor(stores: StoresInstance,private config: GroupDetailAggregationConfig, private aggregationFilterField: string) {
    super(stores)
  }

  @computed get showAggregationFilter(): boolean {
    return !!this.aggregationFilterField
  }

  @computed get selectedAggregationFilterField(): string {
    return this.aggregationFilterField ?? ''
  }

  @computed get availableAggFilterValues(): IAggFilter[] {
    const {aggregationFilterField, catalog: {sectionValuesFor}} = this,
      values = sectionValuesFor(aggregationFilterField) ?? []

    return values.map(({key}) => ({id: key.toString(), label: key.toString()}))
  }

  @computed get selectedAggFiltersSet() {
    const {aggregationFilterField, catalog: {settings: {aggregateDetailsSelectedSections}}} = this
    return new Set<string>(aggregateDetailsSelectedSections[aggregationFilterField] ?? [])
  }

  @computed get selectedAggFilters() {
    return Array.from(this.selectedAggFiltersSet.keys()).map(k => this.aggFilterSectionValueById[k])
  }

  @computed get aggFilterSectionValueById() {
    return _.keyBy(this.availableAggFilterValues, v => v.id)
  }

  @computed get selectedAggFilterValues() {
    const {config} = this
    return _.size(this.selectedAggFilters) ? this.selectedAggFilters.map((s: IAggFilter) => s.label).join(', ') : config?.defaultSelectedLabel ?? ''
  }

  @action setSelectedAggFilters = (item) => {
    const {catalog: {settings: {aggregateDetailsSelectedSections}},selectedAggFiltersSet, aggregationFilterField} = this
    if (!aggregateDetailsSelectedSections[aggregationFilterField]) {
      aggregateDetailsSelectedSections[aggregationFilterField] = []
    }
    const arr = aggregateDetailsSelectedSections[aggregationFilterField]

    if (selectedAggFiltersSet.has(item.id)) {
      aggregateDetailsSelectedSections[aggregationFilterField] = arr.filter(v => v != item.id)
    } else {
      arr.push(item.id)
    }
  }
}

/// endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/AggregateDetails/AggregateDetailsStore.ts